import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import EmployeeNavbar from "../Layout/EmployeeNavbar";

const EmpSettingsPage = () => {
  const savedColor = sessionStorage.getItem("empnavbarColor") || "#080235";

  const [activeTab, setActiveTab] = useState("general");
  const [empnavbarColor] = useState(savedColor);
  const [tempColor, setTempColor] = useState(savedColor);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [dropdownWidth, setDropdownWidth] = useState(0);
  const navigate = useNavigate();

  const handleColorSelect = (color) => {
    setTempColor(color);
    setDropdownOpen(false);
  };

  const handleSave = () => {
    sessionStorage.setItem("empnavbarColor", tempColor);
    alert("Preferences saved successfully!");
    window.location.reload();
  };

  const handleCancel = () => {
    setTempColor(savedColor);
    setDropdownOpen(false);
    alert("Changes canceled.");
    navigate(-1); 
  };

  const colorOptions = [
    { name: "Default Blue", value: "#080235" },
    { name: "Black", value: "#000000" },
    { name: "Dark Gray", value: "#343a40" },
    { name: "Gray", value: "#6c757d" },
    { name: "Light Gray", value: "#adb5bd" },
    { name: "Green", value: "#198754" },
    { name: "Light Green", value: "#28a745" },
    { name: "Teal", value: "#20c997" },
    { name: "Cyan", value: "#17a2b8" },
    { name: "Sky Blue", value: "#87CEEB" },
    { name: "Blue", value: "#0d6efd" },
    { name: "Indigo", value: "#6610f2" },
    { name: "Purple", value: "#6f42c1" },
    { name: "Pink", value: "#d63384" },
    { name: "Red", value: "#dc3545" },
    { name: "Orange", value: "#fd7e14" },
    { name: "Yellow", value: "#ffc107" },
    { name: "Lime", value: "#cddc39" },
    { name: "Brown", value: "#795548" },
    { name: "Maroon", value: "#800000" },
    { name: "Olive", value: "#808000" },
    { name: "Navy", value: "#000080" },
    { name: "Magenta", value: "#ff00ff" },
    { name: "Violet", value: "#8a2be2" },
    { name: "Turquoise", value: "#40e0d0" },
    { name: "Coral", value: "#ff7f50" },
    { name: "Salmon", value: "#fa8072" },
    { name: "Gold", value: "#ffd700" },
    { name: "Silver", value: "#c0c0c0" },
    { name: "Bronze", value: "#cd7f32" },
    { name: "Sky Gray", value: "#b0c4de" },
    { name: "Mint", value: "#98ff98" },
    { name: "Peach", value: "#ffe5b4" },
    { name: "Cream", value: "#fffdd0" },
    { name: "Brick", value: "#b22222" },
    { name: "Persimmon", value: "#e5772a" },
    { name: "Slate Blue", value: "#607799" },
    { name: "Light Sky Blue", value: "#87cefa" },
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // 🔐 Authentication check
  useEffect(() => {
    const token = sessionStorage.getItem("token"); // or sessionStorage
    if (!token) {
      toast.error("You must be logged in to access this page.");
      navigate("/");
    }
  }, [navigate]);

  useEffect(() => {
    if (dropdownRef.current) setDropdownWidth(dropdownRef.current.offsetWidth);
  }, [dropdownRef.current]);

  const tabs = [
    { id: "general", label: "General" },
    { id: "appearance", label: "Appearance" },
  ];

  return (
    <div className="d-flex flex-column vh-100 bg-light">
      {/* Fixed Navbar */}
      <div className="fixed-top">
        <EmployeeNavbar />
      </div>

      {/* Page Content */}
      <div className="flex-grow-1 overflow-auto pt-5 p-4" style={{ marginTop: "80px" }}>
        <div className="mb-4">
          <h2 className="mb-3">Set Preferences</h2>
          <div className="btn-group mb-3">
            <button className="btn btn-primary me-2" onClick={handleSave}>
              Save
            </button>
            <button className="btn btn-secondary" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </div>

        {/* Tabs */}
        <ul
          className="nav nav-tabs mb-4"
          style={{
            backgroundColor: empnavbarColor,
            borderRadius: "0",
            padding: "6px 10px",
          }}
        >
          {tabs.map((tab) => (
            <li className="nav-item" key={tab.id}>
              <button
                className={`nav-link ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  color: activeTab === tab.id ? empnavbarColor : "#fff",
                  backgroundColor: activeTab === tab.id ? "#fff" : "transparent",
                  borderRadius: "6px",
                  marginRight: "8px",
                  fontWeight: "500",
                }}
              >
                {tab.label}
              </button>
            </li>
          ))}
        </ul>

        {/* Tab Content */}
        {activeTab === "appearance" ? (
          <div className="card shadow-sm p-4 mb-4" style={{ maxWidth: "400px" }}>
            <h5 className="mb-3 fw-semibold">Color Appearance</h5>
            <div ref={dropdownRef} className="mb-3 position-relative">
              <label className="form-label fw-bold">Select Appearance Color</label>
              <div
                className="form-control d-flex align-items-center justify-content-between"
                style={{ cursor: "pointer" }}
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                <div className="d-flex align-items-center">
                  <span
                    style={{
                      display: "inline-block",
                      width: "16px",
                      height: "16px",
                      borderRadius: "50%",
                      backgroundColor: tempColor,
                      marginRight: "8px",
                    }}
                  ></span>
                  {colorOptions.find((c) => c.value === tempColor)?.name || "Select Color"}
                </div>
                <span>{dropdownOpen ? "▲" : "▼"}</span>
              </div>

              {dropdownOpen && (
                <div
                  className="position-absolute bg-white border shadow-sm"
                  style={{
                    width: `${dropdownWidth}px`,
                    maxHeight: "200px",
                    overflowY: "auto",
                    zIndex: 1000,
                  }}
                >
                  {colorOptions.map((color) => (
                    <div
                      key={color.value}
                      className="d-flex align-items-center p-2 hover-bg"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleColorSelect(color.value)}
                    >
                      <span
                        style={{
                          display: "inline-block",
                          width: "14px",
                          height: "14px",
                          borderRadius: "50%",
                          backgroundColor: color.value,
                          marginRight: "8px",
                        }}
                      ></span>
                      {color.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="card shadow-sm p-4 mb-4">
            <h5 className="mb-3 fw-semibold">
              {tabs.find((t) => t.id === activeTab)?.label} Settings
            </h5>
            <p>Settings related to {activeTab}.</p>
          </div>
        )}
      </div>

      {/* Custom Styles */}
      <style>{`
        .hover-bg:hover { background-color: #f1f1f1; }
      `}</style>
    </div>
  );
};

export default EmpSettingsPage;
